
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useProject } from "@/hooks/useProject";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { FormValuesEdit, formSchemaEdit } from "../../types";

export function useLaunchForm(launchId: number, launch: any, onClose: () => void) {
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();
  const { projectClient, currentProject } = useProject();

  const form = useForm<FormValuesEdit>({
    resolver: zodResolver(formSchemaEdit),
    defaultValues: {
      form_id: 0,
      id_cliente: 0,
      id_reporte: 0,
      max_respuestas: 0,
      report_lifetime: 0,
      expiration_date: undefined,
      generacion_reporte: undefined,
      report_type: undefined,
      email_sending_type: undefined,
      auto_generate_report: false,
      template_referral_id: undefined,
      notify_on_auto_generate: true,
    },
  });

  // Update form values when launch data is available
  useEffect(() => {
    if (launch) {
      // Convert report_lifetime from seconds to days
      const reportLifetimeDays = launch.report_lifetime != null
        ? Math.round(launch.report_lifetime / (24 * 60 * 60))
        : 0;

      form.reset({
        form_id: launch.form_id ? Number(launch.form_id) : undefined,
        form_mapping_id: launch.form_mapping_id ? Number(launch.form_mapping_id) : undefined,
        id_cliente: Number(launch.id_cliente),
        id_reporte: launch.id_reporte ? Number(launch.id_reporte) : undefined,
        template_v2_id: launch.template_v2_id ? Number(launch.template_v2_id) : undefined,
        max_respuestas: launch.max_respuestas || 0,
        report_lifetime: reportLifetimeDays,
        expiration_date: launch.expiration_date ? new Date(launch.expiration_date) : undefined,
        generacion_reporte: launch.generacion_reporte || undefined,
        report_type: launch.report_type || undefined,
        email_sending_type: launch.email_sending_type || undefined,
        auto_generate_report: launch.auto_generate_report ?? false,
        template_referral_id: launch.template_referral_id ? Number(launch.template_referral_id) : undefined,
        notify_on_auto_generate: launch.notify_on_auto_generate ?? true,
      });
    }
  }, [launch, form]);

  const handleSubmit = async (data: FormValuesEdit) => {
    try {
      setIsLoading(true);

      // Convert days to seconds for database storage
      const reportLifetimeSeconds = data.report_lifetime * 24 * 60 * 60;

      // Build update object with only defined fields
      const updateData: any = {
        id_cliente: data.id_cliente,
        max_respuestas: data.max_respuestas,
        report_lifetime: reportLifetimeSeconds,
        expiration_date: data.expiration_date ? data.expiration_date.toISOString() : null,
        generacion_reporte: data.generacion_reporte || null,
        report_type: data.report_type || null,
        email_sending_type: data.email_sending_type || null,
        // Auto-generation configuration
        auto_generate_report: data.auto_generate_report ?? false,
        template_referral_id: data.template_referral_id || null,
        notify_on_auto_generate: data.notify_on_auto_generate ?? true,
      };

      // Only include V2 fields (form_id column no longer exists in DB)
      if (data.form_mapping_id !== undefined && data.form_mapping_id !== null) {
        updateData.form_mapping_id = data.form_mapping_id;
      }

      if (data.template_v2_id !== undefined && data.template_v2_id !== null) {
        updateData.template_v2_id = data.template_v2_id;
      }

      // Keep id_reporte for backwards compatibility with any remaining legacy launches
      if (data.id_reporte !== undefined && data.id_reporte !== null) {
        updateData.id_reporte = data.id_reporte;
      }

      const { error } = await projectClient
        .from("form_launches")
        .update(updateData)
        .eq("id", launchId);

      if (error) throw error;

      toast.success("Lanzamiento actualizado correctamente");
      queryClient.invalidateQueries({ queryKey: ["launch", currentProject, launchId] });
      queryClient.invalidateQueries({ queryKey: ["launch-edit", currentProject, launchId] });
      queryClient.invalidateQueries({ queryKey: ["launches", currentProject] });
      onClose();
    } catch (error) {
      console.error("Error updating launch:", error);
      toast.error("Error al actualizar el lanzamiento");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    form,
    isLoading,
    handleSubmit
  };
}
